import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import { getUserLabels } from "../services/labelService";
import MetricCardForm from "../components/MetricCardForm";

// Thin wrapper around the shared MetricCardForm that updates an existing card.
// Default export name kept as `EditCaseModal` for backward-compatible imports.
export default function EditCaseModal({ metricId, onClose, onSaved, cases = [] }) {
  const { globalUser } = useAuth();
  const params = useParams();
  const id = metricId || params.id;
  const navigate = useNavigate();

  const [labels, setLabels] = useState([]);
  const [initial, setInitial] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const close = () => (onClose ? onClose() : navigate("/portal/Dashboard"));
  const saved = () => (onSaved ? onSaved() : navigate("/portal/Dashboard"));

  useEffect(() => {
    if (!globalUser) return;
    getUserLabels(globalUser.uid)
      .then(setLabels)
      .catch((err) => console.error("Error fetching labels:", err));
  }, [globalUser]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const snap = await getDoc(doc(db, "metricCard", id));
        if (snap.exists()) setInitial({ id: snap.id, ...snap.data() });
        else toast.error("Metric card not found");
      } catch (err) {
        console.error("Error loading metric card:", err);
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [id]);

  const handleSubmit = async (formData) => {
    if (!globalUser) {
      toast.error("User not authenticated");
      return;
    }
    try {
      // Drop the deprecated single-field shape so updated cards use `filters`.
      await updateDoc(doc(db, "metricCard", id), {
        ...formData,
        field: null,
        caseStatus: null,
        isCustomStatus: null,
        date: null,
        categoryId: null,
      });
      toast.success("Metric card updated");
      saved();
    } catch (error) {
      console.error("Error updating document:", error);
      toast.error("Failed to update metric card");
    }
  };

  if (!loaded) return null;

  return (
    <MetricCardForm
      mode="edit"
      initial={initial}
      labels={labels}
      cases={cases}
      uid={globalUser?.uid}
      onClose={close}
      onSubmit={handleSubmit}
    />
  );
}
